import { type ReactNode } from 'react';
import { ApiError } from '@/api/client';
import { CenteredLoader } from './CenteredLoader';
import { ErrorPanel } from './ErrorPanel';
import { EmptyState } from './EmptyState';

/**
 * <DataState> — boilerplate buster.
 *
 * Pass a TanStack Query result and it renders the correct state:
 *   - loading      → <CenteredLoader>
 *   - error        → <ErrorPanel> (with helpful detail for 401/403/410)
 *   - empty        → optional <EmptyState>
 *   - loaded       → children(data)
 *
 * Usage:
 *   const q = useQuery({ ... });
 *   return <DataState query={q} empty={...}>{(data) => ...}</DataState>;
 */

type QueryLike<T> = {
  isPending?: boolean;
  isLoading?: boolean;
  isError: boolean;
  error: unknown;
  data: T | undefined;
  refetch: () => void;
};

export type DataStateProps<T> = {
  query: QueryLike<T>;
  loadingLabel?: string;
  empty?: { title: string; description?: string; isEmpty?: (data: T) => boolean };
  children: (data: T) => ReactNode;
};

export function DataState<T>({ query, loadingLabel, empty, children }: DataStateProps<T>) {
  const loading = query.isPending ?? query.isLoading ?? false;
  if (loading) return <CenteredLoader label={loadingLabel} />;

  if (query.isError) {
    const e = query.error;
    if (e instanceof ApiError) {
      if (e.status === 403) {
        return <ErrorPanel
          title="You don't have access to this resource"
          detail={e.message}
          severity="warning"
        />;
      }
      if (e.status === 404) {
        return <ErrorPanel
          title="Not found"
          detail={e.message}
          severity="info"
          action={{ label: 'Retry', onClick: () => query.refetch() }}
        />;
      }
      if (e.status === 410) {
        return <ErrorPanel
          title="This is no longer available"
          detail={e.message}
          severity="info"
        />;
      }
    }
    const msg = e instanceof Error ? e.message : 'Unknown error.';
    return <ErrorPanel
      detail={msg}
      action={{ label: 'Retry', onClick: () => query.refetch() }}
    />;
  }

  const data = query.data;
  if (data === undefined) return null;

  if (empty?.isEmpty?.(data)) {
    return <EmptyState title={empty.title} description={empty.description} />;
  }
  if (empty && Array.isArray(data) && data.length === 0) {
    return <EmptyState title={empty.title} description={empty.description} />;
  }

  return <>{children(data)}</>;
}
